import React, { useEffect, useRef, useState } from 'react';
import { Camera, ImagePlus, X, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { easerPost, errorMessage, compressImage } from '../../lib/easerApi';
import { enqueue, shouldQueue, queuedBecause } from '../../lib/offlineQueue';

const KINDS = [['before', 'Avant'], ['after', 'Après'], ['other', 'Autre']];
const MAX_PHOTOS = 8;

const TITLES = { before: 'Photos avant intervention', after: 'Photos après intervention' };

/**
 * Field report: free text + before/after photos, pushed to the Dolibarr intervention sheet.
 * `mode` 'before' | 'after' : ouvert automatiquement après le pointage (type de photo pré-sélectionné, intro dédiée) ;
 * `requirePhoto` : au moins une photo exigée (règle « obligatoire » du back-office).
 */
export function ReportDialog({ open, ev, onClose, onDone, onUnauthorized, mode = null, intro = null, requirePhoto = false }) {
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState([]); // {data, kind}
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => { if (open) { setNote(''); setPhotos([]); } }, [open]);

  const addFiles = async (files, defaultKind) => {
    const list = Array.from(files || []).slice(0, MAX_PHOTOS - photos.length);
    if (!list.length) return;
    setAdding(true);
    try {
      const next = [];
      for (const f of list) {
        try { next.push({ data: await compressImage(f), kind: defaultKind }); } catch { toast.error(`Photo illisible : ${f.name}`); }
      }
      setPhotos((p) => [...p, ...next]);
    } finally {
      setAdding(false);
    }
  };

  const setKind = (i, kind) => setPhotos((p) => p.map((x, idx) => (idx === i ? { ...x, kind } : x)));
  const remove = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (requirePhoto && photos.length === 0) return toast.error('Ajoutez au moins une photo');
    if (!note.trim() && photos.length === 0) return toast.error('Ajoutez un commentaire ou une photo');
    setSaving(true);
    try {
      const res = await easerPost(`/api/easer/events/${ev.id}/report`, { note, photos });
      const up = res.data.uploaded_count;
      toast.success(photos.length
        ? `Compte-rendu envoyé · ${up}/${photos.length} photo${photos.length > 1 ? 's' : ''} déposée${up > 1 ? 's' : ''} sur Dolibarr${res.data.fichinter_ref ? ` (${res.data.fichinter_ref})` : ''}`
        : 'Compte-rendu envoyé');
      onDone();
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      if (shouldQueue(err)) {
        await enqueue({ type: 'report', eventId: ev.id, label: ev.label, payload: { note, photos } });
        toast.info(queuedBecause(err, 'compte-rendu'));
        return onDone();
      }
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const defaultKind = mode || (ev?.status === 'done' || ev?.clock_in_event ? 'after' : 'before');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md w-[95vw] max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="report-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-['Space_Grotesk']" data-testid="report-title">{TITLES[mode] || "Compte-rendu d'intervention"}</DialogTitle>
          <DialogDescription>{ev?.label}{ev?.intervention?.ref ? ` · ${ev.intervention.ref}` : ''} — {intro || "vos photos sont déposées sur la fiche d'intervention Dolibarr."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="report-note">Commentaire{mode ? ' (facultatif)' : ''}</Label>
            <Textarea id="report-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Ce qui a été fait, ce qu'il reste à prévoir, produits manquants..." data-testid="report-note" />
          </div>

          <div className="space-y-2">
            <Label>Photos ({photos.length}/{MAX_PHOTOS})</Label>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files, defaultKind); e.target.value = ''; }} data-testid="report-camera-input" />
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files, defaultKind); e.target.value = ''; }} data-testid="report-gallery-input" />
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" className="h-11" disabled={adding || photos.length >= MAX_PHOTOS} onClick={() => cameraRef.current?.click()} data-testid="report-take-photo">
                {adding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />} Prendre une photo
              </Button>
              <Button type="button" variant="outline" className="h-11" disabled={adding || photos.length >= MAX_PHOTOS} onClick={() => galleryRef.current?.click()} data-testid="report-pick-photo">
                <ImagePlus className="h-4 w-4 mr-1" /> Galerie
              </Button>
            </div>
            {photos.length > 0 && (
              <ul className="grid grid-cols-2 gap-2" data-testid="report-photo-list">
                {photos.map((p, i) => (
                  <li key={i} className="relative rounded-lg border border-border overflow-hidden">
                    <img src={p.data} alt={`Photo ${i + 1}`} className="h-28 w-full object-cover" />
                    <button type="button" onClick={() => remove(i)} aria-label="Retirer" className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid="report-photo-remove"><X className="h-4 w-4" /></button>
                    <div className="flex p-1 gap-1 bg-card">
                      {KINDS.map(([id, label]) => (
                        <button key={id} type="button" onClick={() => setKind(i, id)} data-testid={`report-photo-kind-${id}`}
                          className={`flex-1 h-7 rounded text-[11px] font-medium transition-colors ${p.kind === id ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:bg-[hsl(var(--secondary))]'}`}>{label}</button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="report-cancel">{mode ? 'Plus tard' : 'Annuler'}</Button>
          <Button onClick={submit} disabled={saving || adding} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="report-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
